import React from 'react';
import { Box, Text, Flex, Badge } from '@chakra-ui/core';
import { useTranslation } from 'react-i18next';
import { BsPersonCheckFill } from 'react-icons/bs';
import { Candidate, I9StatusList } from '../../../../../firebase/firestore/documents/candidate';
import colors from '../../../../../styles/colors';
import PermissionWrapper from '../../../../../services/permission/PermissionWrapper';
import { EmployeePermissionsEnum } from '../../../../administration/team/teamPermission/teamPermission.constant';
import { PERMISSION_MODE } from '../../../../../services/permission/permission.interface';
import { useStoreActions, useStoreState } from '../../../../../models/hooks';

export type I9StatusProps = {
  selectedCandidate: Candidate | undefined;
};

const SeekerI9Status = ({ selectedCandidate }: I9StatusProps): JSX.Element => {
  const { t } = useTranslation('i9Verification');

  const superOwners = useStoreState((s) => s.app.accounts?.superOwners);
  const userId = useStoreState((s) => s.app.user?.id);

  const getWorkbrightLink = useStoreActions((actions) => actions.candidate.getWorkbrightLink);

  const getColorByStatus = (status: string | undefined) => {
    switch (status) {
      case 'REQUESTED':
      case 'NEW_FORM_SUBMISSION_REQUESTED':
      case 'I9_RESTARTED':
        return colors.lightBlue[100];
      case 'ALL_FORMS_RECEIVED':
      case 'ALL_REQUIRED_SUBMISSION_FINALIZED':
        return colors.green[100];
      case 'FORM_SUBMITTED':
      case 'IN_PROGRESS':
        return colors.yellow[100];
      case 'SUBMISSION_REJECTED':
        return colors.red[100];
      default:
        return colors.yellow[50];
    }
  };

  const handleWorkbrightLink = async (id: string) => {
    if (!id) return;
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const response = await getWorkbrightLink({ candidateId: id });

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (response?.link && response?.link !== undefined) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        window.open(response.link, '_blank');
      } else {
        console.error('No valid url found.');
      }
    } catch (e) {
      console.log('Error while getting the link', e);
    }
  };

  const isSuperAdmin = (): boolean => {
    if (!superOwners || !userId) {
      return false;
    }
    return superOwners?.includes(userId);
  };

  return (
    <Box>
      <PermissionWrapper permissions={[EmployeePermissionsEnum.I9_REVIEW]} mode={PERMISSION_MODE.disable} enableTooltip>
        <Badge
          cursor="pointer"
          borderRadius="5px"
          variant="subtle"
          textTransform="none"
          data-testid="I9StatusButton"
          backgroundColor={getColorByStatus(selectedCandidate?.workbright?.status)}
          onClick={() => {
            if (
              isSuperAdmin() &&
              (selectedCandidate?.workbright?.status === I9StatusList.ALL_FORMS_RECEIVED ||
                selectedCandidate?.workbright?.status === I9StatusList.ALL_REQUIRED_SUBMISSION_FINALIZED)
            ) {
              // eslint-disable-next-line @typescript-eslint/no-floating-promises
              handleWorkbrightLink(selectedCandidate?.id);
            }
          }}
        >
          <Flex alignItems="center" padding="4px">
            <BsPersonCheckFill size={18} color={colors.persianBlue} />
            <Text ml={1} color={colors.black}>
              {t(`status.${selectedCandidate?.workbright?.status}`)}
            </Text>
          </Flex>
        </Badge>
      </PermissionWrapper>
    </Box>
  );
};

export default SeekerI9Status;
